import React, { useState, useMemo, useRef } from 'react';
import { GripVertical, ArrowUp, ArrowDown, X } from 'lucide-react';

export function SortableQuestion({ question, value, onChange, allAnswers = {} }) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [otherText, setOtherText] = useState('');
  const containerRef = useRef(null);

  // Resolve options - support both static arrays and dynamic functions
  const options = useMemo(() => {
    if (typeof question.options === 'function') {
      return question.options(allAnswers);
    }
    return question.options;
  }, [question.options, allAnswers]);

  // Value structure: { selections: ['option1', 'option2'], order: ['option1', 'option2'] }
  const currentValue = value || { selections: [], order: [] };
  const { selections = [], order = [] } = currentValue;

  const maxSelections = question.maxSelections || Infinity;
  const minSelections = question.minSelections || 1;
  const hasOtherOption = question.allowOther === true;

  // Check if "outro" or custom text is in selections
  const hasOutroSelected = selections.some(val => {
    // Check if it's the literal "outro" or a custom value not in options
    if (val === 'outro') return true;
    const isKnownOption = options.some(opt => opt.value === val);
    return !isKnownOption;
  });

  // Get the custom text if it exists
  React.useEffect(() => {
    if (hasOtherOption && hasOutroSelected) {
      const customValue = selections.find(val => {
        if (val === 'outro') return false;
        const isKnownOption = options.some(opt => opt.value === val);
        return !isKnownOption;
      });
      if (customValue) {
        setOtherText(customValue);
      }
    }
  }, [selections, hasOtherOption, hasOutroSelected, options]);

  // Scroll to navigation buttons
  const scrollToNavigationButtons = () => {
    // Find the navigation buttons container
    const questionCard = containerRef.current?.closest('.bg-white.rounded-2xl');
    if (questionCard) {
      const navButtons = questionCard.querySelector('button[class*="bg-gradient-to-r"]');
      if (navButtons) {
        // Get the footer height to offset scroll
        const footer = document.querySelector('footer');
        const footerHeight = footer ? footer.offsetHeight : 0;

        // Calculate the position to scroll to
        const navButtonsRect = navButtons.getBoundingClientRect();
        const scrollOffset = window.pageYOffset + navButtonsRect.top - (window.innerHeight - footerHeight - 100);

        // Scroll to position that shows the button above the footer with extra padding
        window.scrollTo({
          top: scrollOffset,
          behavior: 'smooth'
        });
      }
    }
  };

  // Handle selecting/unselecting an option
  const handleToggleOption = (optionValue) => {
    let newSelections = [...selections];
    let newOrder = [...order];

    // Special handling for "outro"
    if (optionValue === 'outro') {
      if (hasOutroSelected) {
        // Remove "outro" or custom text
        newSelections = newSelections.filter(v => {
          if (v === 'outro') return false;
          const isKnownOption = options.some(opt => opt.value === v);
          return isKnownOption;
        });
        newOrder = newOrder.filter(v => {
          if (v === 'outro') return false;
          const isKnownOption = options.some(opt => opt.value === v);
          return isKnownOption;
        });
        setOtherText('');
      } else {
        // Add "outro"
        if (newSelections.length >= maxSelections) {
          return; // Max reached
        }
        const valueToAdd = otherText || 'outro';
        newSelections.push(valueToAdd);
        newOrder.push(valueToAdd);
      }
    } else {
      // Regular option
      if (selections.includes(optionValue)) {
        // Remove from selections and order
        newSelections = newSelections.filter(v => v !== optionValue);
        newOrder = newOrder.filter(v => v !== optionValue);
      } else {
        // Add to selections
        if (newSelections.length >= maxSelections) {
          return; // Max reached
        }
        newSelections.push(optionValue);
        newOrder.push(optionValue);
      }
    }

    onChange({ selections: newSelections, order: newOrder });

    // Scroll to navigation buttons if we've reached the limit
    if (newSelections.length >= maxSelections && optionValue !== 'outro') {
      setTimeout(() => {
        scrollToNavigationButtons();
      }, 100);
    }
  };

  // Handle outro text change
  const handleOtherTextChange = (e) => {
    const text = e.target.value;
    setOtherText(text);

    // Update selections and order to replace "outro" with custom text
    if (hasOutroSelected) {
      const newSelections = selections.map(v => {
        if (v === 'outro') return text || 'outro';
        const isKnownOption = options.some(opt => opt.value === v);
        return isKnownOption ? v : text || 'outro';
      });

      const newOrder = order.map(v => {
        if (v === 'outro') return text || 'outro';
        const isKnownOption = options.some(opt => opt.value === v);
        return isKnownOption ? v : text || 'outro';
      });

      onChange({ selections: newSelections, order: newOrder });
    }
  };

  // Handle reordering with arrows
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newOrder = [...order];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    onChange({ selections, order: newOrder });
  };

  const handleMoveDown = (index) => {
    if (index === order.length - 1) return;
    const newOrder = [...order];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    onChange({ selections, order: newOrder });
  };

  // Handle drag and drop
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newOrder = [...order];
    const draggedItem = newOrder[draggedIndex];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(index, 0, draggedItem);

    setDraggedIndex(index);
    onChange({ selections, order: newOrder });
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const getOptionLabel = (optionValue) => {
    const option = options.find(opt => opt.value === optionValue);
    if (option) {
      return option.label;
    }
    // If not in options, it's custom "outro" text
    return optionValue === 'outro' ? 'Outro' : optionValue;
  };

  const isMaxReached = selections.length >= maxSelections;

  return (
    <div ref={containerRef} className="space-y-6">
      {/* Selection Phase */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Selecione suas opções {minSelections > 0 && `(mínimo ${minSelections})`}
          {maxSelections < Infinity && ` - máximo ${maxSelections}`}
        </h3>
        <div className="space-y-2">
          {options.map((opt) => {
            const isSelected = selections.includes(opt.value);
            const isDisabled = !isSelected && isMaxReached;

            return (
              <label
                key={opt.value}
                className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition ${
                  isDisabled
                    ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                    : isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                }`}
              >
                <input
                  type="checkbox"
                  value={opt.value}
                  checked={isSelected}
                  onChange={() => handleToggleOption(opt.value)}
                  disabled={isDisabled}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="ml-3 text-gray-700 font-medium">{opt.label}</span>
                {isSelected && (
                  <span className="ml-auto text-xs bg-blue-600 text-white px-2 py-1 rounded">
                    #{order.indexOf(opt.value) + 1}
                  </span>
                )}
              </label>
            );
          })}

          {/* "Outro" option if allowOther is true */}
          {hasOtherOption && (
            <label
              className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition ${
                !hasOutroSelected && isMaxReached
                  ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                  : hasOutroSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              <input
                type="checkbox"
                value="outro"
                checked={hasOutroSelected}
                onChange={() => handleToggleOption('outro')}
                disabled={!hasOutroSelected && isMaxReached}
                className="w-4 h-4 text-blue-600 mt-0.5"
              />
              <div className="ml-3 flex-1">
                <span className="text-gray-700 font-medium">Outro</span>
                {hasOutroSelected && (
                  <>
                    <input
                      type="text"
                      value={otherText}
                      onChange={handleOtherTextChange}
                      placeholder="Especifique..."
                      className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {hasOutroSelected && (
                      <span className="inline-block mt-1 text-xs bg-blue-600 text-white px-2 py-1 rounded">
                        #{order.findIndex(v => v === 'outro' || !options.some(opt => opt.value === v)) + 1}
                      </span>
                    )}
                  </>
                )}
              </div>
            </label>
          )}
        </div>
      </div>

      {/* Ranking Phase */}
      {order.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Arraste para ordenar por preferência (1 = mais preferido)
          </h3>
          <div className="space-y-2 bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
            {order.map((optionValue, index) => (
              <div
                key={optionValue}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-3 p-3 bg-white border-2 rounded-lg cursor-move hover:border-purple-400 transition ${
                  draggedIndex === index ? 'opacity-50 border-purple-500' : 'border-gray-300'
                }`}
              >
                {/* Drag Handle */}
                <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />

                {/* Rank Number */}
                <div className="flex items-center justify-center w-8 h-8 bg-purple-600 text-white font-bold rounded-full flex-shrink-0">
                  {index + 1}
                </div>

                {/* Option Label */}
                <span className="flex-1 text-gray-700 font-medium">
                  {getOptionLabel(optionValue)}
                </span>

                {/* Arrow Controls */}
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className={`p-1 rounded ${
                      index === 0
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-purple-600 hover:bg-purple-100'
                    }`}
                    title="Mover para cima"
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === order.length - 1}
                    className={`p-1 rounded ${
                      index === order.length - 1
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-purple-600 hover:bg-purple-100'
                    }`}
                    title="Mover para baixo"
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleOption(optionValue)}
                    className="p-1 text-red-600 hover:bg-red-100 rounded ml-1"
                    title="Remover"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            💡 Arraste os itens ou use as setas ↑↓ para reordenar
          </p>
        </div>
      )}

      {/* Helper Text */}
      {selections.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          Selecione pelo menos {minSelections} {minSelections === 1 ? 'opção' : 'opções'} acima
        </p>
      )}
    </div>
  );
}
